/**
 * Entry point for every page.
 *
 * Tool pages carry `<div data-editor-root data-lang data-tool>`; the editor mounts there.
 * Printable-form pages carry `<div data-print-form-root>` and load the form generator instead.
 * Info pages (home, about, privacy, contact, the forms hub) have neither element and load
 * nothing else, so their content is the static HTML on its own.
 */

import './styles/main.css';
import { isLang } from './i18n';
// Tiny and pure (the route table already depends on it); the generator itself is lazy.
import { isFormKind } from './print-forms/model';
import { TOOLS, type Tool } from './site/routes';

function isTool(value: unknown): value is Tool {
  return typeof value === 'string' && (TOOLS as readonly string[]).includes(value);
}

function hideNoScriptNotes(): void {
  // The static markup shows a "JavaScript required" note by default; JS having run is the
  // only reliable signal that it can be hidden.
  for (const node of document.querySelectorAll<HTMLElement>('[data-hide-when-ready]')) {
    node.hidden = true;
  }
}

/**
 * Printable-form pages carry `<div data-print-form-root data-lang data-form>`. The generator,
 * fontkit and PDF.js are behind a dynamic import, and the font is fetched only from here, so
 * no other page downloads any of it.
 */
async function mountPrintForm(host: HTMLElement): Promise<void> {
  const { lang, form } = host.dataset;
  if (!isLang(lang) || !isFormKind(form)) {
    host.textContent = 'Form could not start: unknown language or form.';
    return;
  }
  const { PrintFormApp } = await import('./print-forms/ui');
  const app = new PrintFormApp(host, lang, form);
  host.removeAttribute('data-print-pending');
  hideNoScriptNotes();

  // Read-only hook for the browser tests: phase, file name and a fingerprint of the current
  // artifact. Never the typed text or the bytes.
  (window as unknown as Record<string, unknown>).__printFormDebug = { state: () => app.debugState() };
}

async function mount(): Promise<void> {
  const printHost = document.querySelector<HTMLElement>('[data-print-form-root]');
  if (printHost) {
    await mountPrintForm(printHost);
    return;
  }

  const host = document.querySelector<HTMLElement>('[data-editor-root]');
  if (!host) return;

  const { lang, tool } = host.dataset;
  if (!isLang(lang) || !isTool(tool)) {
    // A mis-generated page should say so rather than silently show nothing.
    host.textContent = 'Editor could not start: unknown language or tool.';
    return;
  }

  // pdf-lib and PDF.js are only needed on tool pages, so they live behind a dynamic
  // import; the home, about, privacy and contact pages stay small.
  const { Editor } = await import('./ui/editor');
  const editor = new Editor(host, lang, tool);
  host.removeAttribute('data-editor-pending');

  hideNoScriptNotes();

  // Small read-only hook used by the browser tests to cross-check on-screen state against
  // the model. It exposes counts and rotations only — never file bytes or names.
  (window as unknown as Record<string, unknown>).__pdfToolsDebug = {
    state: () => editor.debugState(),
  };
}

function start(): void {
  void mount().catch((error: unknown) => {
    const host = document.querySelector<HTMLElement>('[data-editor-root], [data-print-form-root]');
    if (host) {
      host.textContent = `Editor could not start: ${error instanceof Error ? error.message : String(error)}`;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
